import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { InventoryProvider } from './context/InventoryContext';
import { supabase } from './lib/supabase';
import Layout from './components/layout/Layout';
import AuthPage from './pages/AuthPage';
import DashboardPage from './pages/DashboardPage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CategoriesPage from './pages/CategoriesPage';
import CategoryDetailPage from './pages/CategoryDetailPage';
import TransactionsPage from './pages/TransactionsPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import SuppliersPage from './pages/SuppliersPage';
import SupplierFormPage from './pages/SupplierFormPage';
import PurchaseOrdersPage from './pages/PurchaseOrdersPage';
import PurchaseOrderDetailPage from './pages/PurchaseOrderDetailPage';
import SalesOrdersPage from './pages/SalesOrdersPage';
import SalesOrderDetailPage from './pages/SalesOrderDetailPage';
import ReceivingPage from './pages/ReceivingPage';
import ShippingPage from './pages/ShippingPage';
import NotificationsPage from './pages/NotificationsPage';
import AuditTrailPage from './pages/AuditTrailPage';
import LocationsPage from './pages/LocationsPage';
import LocationTransfersPage from './pages/LocationTransfersPage';
import CustomerPortal from './pages/customer/CustomerPortal';
import SupplierPortal from './pages/supplier/SupplierPortal';
import RDInventoryPage from './pages/RDInventoryPage';
import RDProductDetailPage from './pages/RDProductDetailPage';
import RDWasteManagementPage from './pages/RDWasteManagementPage';
import RDWasteDetailPage from './pages/RDWasteDetailPage';
import ToolsPage from './pages/ToolsPage';
import ToolDetailPage from './pages/ToolDetailPage';
import ProductsManufacturingPage from './pages/ProductsManufacturingPage';
import ProductManufacturingDetailPage from './pages/ProductManufacturingDetailPage';
import MismatchInventoryPage from './pages/MismatchInventoryPage';
import WarrantiesPage from './pages/WarrantiesPage';
import WarrantyDetailPage from './pages/WarrantyDetailPage';
import UserManagementPage from './pages/UserManagementPage';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }
  
  return user ? <>{children}</> : <Navigate to="/auth" />;
};

const AppRoutes: React.FC = () => {
  const { user } = useAuth();

  // Initialize color scheme on app load
  useEffect(() => {
    const initializeColorScheme = async () => {
      try {
        // First check localStorage for immediate application
        const savedScheme = localStorage.getItem('user-color-scheme');
        if (savedScheme) {
          applyColorScheme(savedScheme);
        }

        // Then check database if user is authenticated
        if (user) {
          const { data, error } = await supabase
            .from('user_settings')
            .select('color_scheme')
            .eq('user_id', user.id)
            .single();

          if (data?.color_scheme && data.color_scheme !== savedScheme) {
            applyColorScheme(data.color_scheme);
            localStorage.setItem('user-color-scheme', data.color_scheme);
          }
        }
      } catch (error) {
        console.error('Error loading color scheme:', error);
        // Apply default scheme on error
        applyColorScheme('ocean-breeze');
      }
    };

    initializeColorScheme();
  }, [user]);

  const applyColorScheme = (schemeId: string) => {
    const root = document.documentElement;
    const body = document.body;
    
    // Remove all existing theme classes
    const removeThemeClasses = (element: HTMLElement) => {
      const classes = Array.from(element.classList);
      classes.forEach(cls => {
        if (cls.startsWith('theme-')) {
          element.classList.remove(cls);
        }
      });
    };
    
    removeThemeClasses(root);
    removeThemeClasses(body);
    
    // Add new theme class
    root.classList.add(`theme-${schemeId}`);
    body.classList.add(`theme-${schemeId}`);
    
    // Handle dark mode for specific themes
    const darkThemes = [
      'midnight-professional', 'charcoal-elite', 'dark-ocean', 'carbon-fiber',
      'night-forest', 'cosmic-purple', 'neon-nights', 'aurora-borealis',
      'moonlight-mist', 'deep-forest', 'cosmic-dream', 'neon-synthwave',
      'graffiti-urban', 'high-contrast-dark'
    ];
    
    if (darkThemes.includes(schemeId)) {
      root.classList.add('dark');
      body.classList.add('dark');
    } else {
      root.classList.remove('dark');
      body.classList.remove('dark');
    }
    
    console.log(`Applied color scheme: ${schemeId}`);
  };

  return (
    <Routes>
      <Route path="/auth" element={user ? <Navigate to="/" /> : <AuthPage />} />
      <Route path="/customer/:orderId" element={<CustomerPortal />} />
      <Route path="/supplier/:orderId" element={<SupplierPortal />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout>
              <Routes>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/mismatch-inventory" element={<MismatchInventoryPage />} />
                <Route path="/supplies" element={<ProductsPage />} />
                <Route path="/supplies/:id" element={<ProductDetailPage />} />
                <Route path="/products" element={<ProductsManufacturingPage />} />
                <Route path="/products/:id" element={<ProductManufacturingDetailPage />} />
                <Route path="/rd-inventory" element={<RDInventoryPage />} />
                <Route path="/rd-inventory/:id" element={<RDProductDetailPage />} />
                <Route path="/rd-waste" element={<RDWasteManagementPage />} />
                <Route path="/rd-waste/:id" element={<RDWasteDetailPage />} />
                <Route path="/tools" element={<ToolsPage />} />
                <Route path="/tools/:id" element={<ToolDetailPage />} />
                <Route path="/categories" element={<CategoriesPage />} />
                <Route path="/categories/:id" element={<CategoryDetailPage />} />
                <Route path="/transactions" element={<TransactionsPage />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/sales-orders" element={<SalesOrdersPage />} />
                <Route path="/sales-orders/:id" element={<SalesOrderDetailPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/suppliers" element={<SuppliersPage />} />
                <Route path="/suppliers/new" element={<SupplierFormPage />} />
                <Route path="/suppliers/:id/edit" element={<SupplierFormPage />} />
                <Route path="/purchase-orders" element={<PurchaseOrdersPage />} />
                <Route path="/purchase-orders/:id" element={<PurchaseOrderDetailPage />} />
                <Route path="/sales-orders" element={<SalesOrdersPage />} />
                <Route path="/sales-orders/:id" element={<SalesOrderDetailPage />} />
                <Route path="/receiving" element={<ReceivingPage />} />
                <Route path="/shipping" element={<ShippingPage />} />
                <Route path="/warranties" element={<WarrantiesPage />} />
                <Route path="/warranties/:id" element={<WarrantyDetailPage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/audit-trail" element={<AuditTrailPage />} />
                <Route path="/locations" element={<LocationsPage />} />
                <Route path="/location-transfers" element={<LocationTransfersPage />} />
                <Route path="/user-management" element={<UserManagementPage />} />
              </Routes>
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <InventoryProvider>
        <Router>
          <AppRoutes />
        </Router>
      </InventoryProvider>
    </AuthProvider>
  );
};

export default App;